import { useState, useEffect, useCallback } from 'react';
import type {
  WeatherData,
  WeatherForecast,
  WeatherAlert,
  PackingRecommendation,
  WeatherInsight,
  TripWeatherSummary
} from '../types/weather';

const WEATHER_API_KEY = import.meta.env.VITE_OPENWEATHER_API_KEY || '';
const BASE_URL = 'https://api.openweathermap.org/data/2.5';

interface UseWeatherOptions {
  enabled?: boolean;
  unit?: 'metric' | 'imperial';
  days?: number;
}

interface UseWeatherReturn {
  currentWeather: WeatherData | null;
  forecast: WeatherForecast | null;
  loading: boolean;
  error: string | null;
  fetchWeather: (lat: number, lng: number, locationName: string) => Promise<void>;
  getPackingRecommendations: () => PackingRecommendation[];
  getWeatherInsights: () => WeatherInsight[];
  getTripSummary: () => TripWeatherSummary | null;
}

export function useWeather(options: UseWeatherOptions = {}): UseWeatherReturn {
  const { enabled = true, unit = 'metric', days = 7 } = options;

  const [currentWeather, setCurrentWeather] = useState<WeatherData | null>(null);
  const [forecast, setForecast] = useState<WeatherForecast | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchWeather = useCallback(async (lat: number, lng: number, locationName: string) => {
    if (!enabled || !WEATHER_API_KEY) {
      setCurrentWeather(generateMockWeather(locationName));
      setForecast(generateMockForecast(locationName, days));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const currentResponse = await fetch(
        `${BASE_URL}/weather?lat=${lat}&lon=${lng}&appid=${WEATHER_API_KEY}&units=${unit}`
      );

      if (!currentResponse.ok) {
        throw new Error('Failed to fetch weather data');
      }

      const currentData = await currentResponse.json();
      setCurrentWeather(transformCurrentWeather(currentData, locationName));

      const forecastResponse = await fetch(
        `${BASE_URL}/forecast?lat=${lat}&lon=${lng}&appid=${WEATHER_API_KEY}&units=${unit}&cnt=${days * 8}`
      );

      if (!forecastResponse.ok) {
        throw new Error('Failed to fetch forecast data');
      }

      const forecastData = await forecastResponse.json();
      setForecast(transformForecast(forecastData, locationName, days));

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setCurrentWeather(generateMockWeather(locationName));
      setForecast(generateMockForecast(locationName, days));
    } finally {
      setLoading(false);
    }
  }, [enabled, unit, days]);

  const getPackingRecommendations = useCallback((): PackingRecommendation[] => {
    if (!currentWeather) return [];

    const recommendations: PackingRecommendation[] = [];
    const temp = currentWeather.temperature;
    const condition = currentWeather.condition.toLowerCase();

    if (temp < 10) {
      recommendations.push({
        item: 'Warm jacket',
        category: 'clothing',
        reason: 'Temperature below 10°C',
        priority: 'essential'
      });
    } else if (temp < 20) {
      recommendations.push({
        item: 'Light jacket or sweater',
        category: 'clothing',
        reason: 'Temperature between 10-20°C',
        priority: 'essential'
      });
    }

    if (temp > 25) {
      recommendations.push({
        item: 'Sunscreen SPF 30+',
        category: 'health',
        reason: 'Warm weather, high UV expected',
        priority: 'essential'
      });
    }

    if (condition.includes('rain')) {
      recommendations.push({
        item: 'Umbrella or rain jacket',
        category: 'clothing',
        reason: 'Rain expected',
        priority: 'essential'
      });
    }

    recommendations.push({
      item: 'First aid kit',
      category: 'health',
      reason: 'Always essential for travel',
      priority: 'essential'
    });

    return recommendations;
  }, [currentWeather]);

  const getWeatherInsights = useCallback((): WeatherInsight[] => {
    if (!currentWeather) return [];

    const insights: WeatherInsight[] = [];
    const condition = currentWeather.condition.toLowerCase();
    const temp = currentWeather.temperature;

    if (temp > 30) {
      insights.push({
        type: 'warning',
        title: 'High Temperature',
        description: 'Stay hydrated and avoid prolonged sun exposure.',
        severity: 'high'
      });
    }

    if (condition.includes('rain')) {
      insights.push({
        type: 'warning',
        title: 'Rain Expected',
        description: 'Consider indoor activities or bring waterproof gear.',
        severity: 'medium'
      });
    }

    if (temp >= 20 && temp <= 28 && !condition.includes('rain')) {
      insights.push({
        type: 'opportunity',
        title: 'Excellent Weather',
        description: 'Perfect conditions for outdoor activities and sightseeing.',
        severity: 'info'
      });
    }

    return insights;
  }, [currentWeather]);

  const getTripSummary = useCallback((): TripWeatherSummary | null => {
    if (!forecast || forecast.daily.length === 0) return null;

    const temps = forecast.daily.map(d => d.temperature);
    const rainDays = forecast.daily.filter(d =>
      d.condition.toLowerCase().includes('rain')
    ).length;

    const avgTemp = temps.reduce((a, b) => a + b, 0) / temps.length;
    const minTemp = Math.min(...temps);
    const maxTemp = Math.max(...temps);

    let overallCondition: TripWeatherSummary['overallCondition'];
    if (avgTemp >= 20 && avgTemp <= 28 && rainDays <= 2) {
      overallCondition = 'excellent';
    } else if (avgTemp >= 15 && avgTemp <= 30 && rainDays <= 4) {
      overallCondition = 'good';
    } else {
      overallCondition = 'moderate';
    }

    return {
      overallCondition,
      averageTemperature: avgTemp,
      temperatureRange: { min: minTemp, max: maxTemp },
      rainDays,
      recommendedClothing: [],
      packingTips: [],
      weatherAlerts: []
    };
  }, [forecast]);

  return {
    currentWeather,
    forecast,
    loading,
    error,
    fetchWeather,
    getPackingRecommendations,
    getWeatherInsights,
    getTripSummary
  };
}

function transformCurrentWeather(data: any, locationName: string): WeatherData {
  return {
    location: locationName,
    date: new Date().toISOString(),
    temperature: Math.round(data.main.temp),
    temperatureMin: Math.round(data.main.temp_min),
    temperatureMax: Math.round(data.main.temp_max),
    humidity: data.main.humidity,
    condition: data.weather[0].main,
    conditionIcon: data.weather[0].description,
    windSpeed: Math.round(data.wind.speed * 3.6),
    precipitation: data.rain?.['1h'] || 0,
    uvIndex: 0,
    sunrise: new Date(data.sys.sunrise * 1000).toISOString(),
    sunset: new Date(data.sys.sunset * 1000).toISOString()
  };
}

function transformForecast(data: any, locationName: string, days: number): WeatherForecast {
  const daily: WeatherData[] = [];
  const processedDates = new Set<string>();

  for (const item of data.list) {
    const date = new Date(item.dt * 1000).toISOString().split('T')[0];

    if (!processedDates.has(date) && daily.length < days) {
      processedDates.add(date);
      daily.push({
        location: locationName,
        date: date,
        temperature: Math.round(item.main.temp),
        temperatureMin: Math.round(item.main.temp_min),
        temperatureMax: Math.round(item.main.temp_max),
        humidity: item.main.humidity,
        condition: item.weather[0].main,
        conditionIcon: item.weather[0].description,
        windSpeed: Math.round(item.wind.speed * 3.6),
        precipitation: item.rain?.['3h'] || 0,
        uvIndex: 0,
        sunrise: '',
        sunset: ''
      });
    }
  }

  return {
    location: locationName,
    startDate: daily[0]?.date || new Date().toISOString().split('T')[0],
    endDate: daily[daily.length - 1]?.date || new Date().toISOString().split('T')[0],
    daily,
    source: 'openweathermap',
    lastUpdated: new Date().toISOString()
  };
}

function generateMockWeather(locationName: string): WeatherData {
  return {
    location: locationName,
    date: new Date().toISOString(),
    temperature: 22,
    temperatureMin: 18,
    temperatureMax: 26,
    humidity: 65,
    condition: 'Partly Cloudy',
    conditionIcon: 'partly-cloudy',
    windSpeed: 15,
    precipitation: 0,
    uvIndex: 6,
    sunrise: '06:30',
    sunset: '19:45'
  };
}

function generateMockForecast(locationName: string, days: number): WeatherForecast {
  const daily: WeatherData[] = [];
  const today = new Date();

  for (let i = 0; i < days; i++) {
    const date = new Date(today);
    date.setDate(date.getDate() + i);

    const conditions = ['Sunny', 'Partly Cloudy', 'Cloudy', 'Light Rain', 'Sunny'];
    const condition = conditions[i % conditions.length];
    const baseTemp = 22 + Math.sin(i * 0.5) * 5;

    daily.push({
      location: locationName,
      date: date.toISOString().split('T')[0],
      temperature: Math.round(baseTemp),
      temperatureMin: Math.round(baseTemp - 5),
      temperatureMax: Math.round(baseTemp + 4),
      humidity: 60 + Math.random() * 20,
      condition,
      conditionIcon: condition.toLowerCase().replace(' ', '-'),
      windSpeed: 10 + Math.random() * 20,
      precipitation: condition.includes('Rain') ? Math.random() * 10 : 0,
      uvIndex: condition.includes('Sunny') ? 7 : 4,
      sunrise: '06:30',
      sunset: '19:45'
    });
  }

  return {
    location: locationName,
    startDate: daily[0].date,
    endDate: daily[daily.length - 1].date,
    daily,
    source: 'mock',
    lastUpdated: new Date().toISOString()
  };
}

export default useWeather;
